/**
 * THE INVERSION between the layers panel and the store, in one place.
 *
 * `LayersPanel` lists the TOP of the z-stack first, because that is what every
 * design tool does. The store paints on ASCENDING `zIndex`, so the two orders are
 * mirror images, and the panel also carries a synthetic locked `Background` row
 * that is not a store layer at all. Getting either of those backwards silently
 * reverses a user's stack instead of failing, which is why the arithmetic lives
 * here with tests rather than inline in the page.
 */
export interface OrderableLayer {
  id: string;
  locked?: boolean;
}

/**
 * Move the layer at panel index `from` to panel index `to`.
 *
 * @returns the full stack as ids BOTTOM-FIRST, ready for `setLayerOrder`, or
 * `null` when the move is not a move (out of range, or onto itself).
 */
export function nextLayerOrder(
  panelLayers: readonly OrderableLayer[],
  from: number,
  to: number,
): string[] | null {
  const stack = panelLayers.filter((layer) => !layer.locked).map((layer) => layer.id);
  if (from === to) return null;
  if (from < 0 || from >= stack.length) return null;
  if (to < 0 || to >= stack.length) return null;
  const [moved] = stack.splice(from, 1);
  if (!moved) return null;
  stack.splice(to, 0, moved);
  return stack.reverse();
}
